import React, { useState, useEffect } from 'react';
import { Type, CheckCircle, XCircle, FileText, Database, Copy, RefreshCw, BarChart3, Smile, EyeOff, Hash, Palette } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';
import EmojiPicker from './EmojiPicker';
import StringObfuscator from './text/StringObfuscator';
import NumeronymGenerator from './text/NumeronymGenerator';
import AsciiArtGenerator from './text/AsciiArtGenerator';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'json-formatter', name: 'Formatador de JSON', path: '/text/json-formatter' },
    { id: 'json-validator', name: 'Validador de JSON', path: '/text/json-validator' },
    { id: 'xml-formatter', name: 'Formatador de XML', path: '/text/xml-formatter' },
    { id: 'xml-validator', name: 'Validador de XML', path: '/text/xml-validator' },
    { id: 'sql-formatter', name: 'Formatador de SQL', path: '/text/sql-formatter' },
    { id: 'csv-formatter', name: 'Formatador de CSV', path: '/text/csv-formatter' },
    { id: 'text-converter', name: 'Conversor de Texto', path: '/text/text-converter' },
    { id: 'emoji-picker', name: 'Seletor de Emojis', path: '/text/emoji-picker' },
    { id: 'string-obfuscator', name: 'Ofuscador de String', path: '/text/string-obfuscator' },
    { id: 'numeronym-generator', name: 'Gerador de Numerônimos', path: '/text/numeronym-generator' },
    { id: 'ascii-art-generator', name: 'Gerador de ASCII Art', path: '/text/ascii-art-generator' },
    { id: 'font-style-generator', name: 'Gerador de Textos Estilizados', path: '/text/font-style-generator' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Texto', tool: match ? match.name : null };
}

const Text = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'json-formatter', name: 'Formatador de JSON', icon: FileText, path: '/text/json-formatter', description: 'Formatação e validação', status: 'active' },
    { id: 'json-validator', name: 'Validador de JSON', icon: CheckCircle, path: '/text/json-validator', description: 'Verificação de sintaxe', status: 'active' },
    { id: 'xml-formatter', name: 'Formatador de XML', icon: FileText, path: '/text/xml-formatter', description: 'Formatação e validação', status: 'active' },
    { id: 'xml-validator', name: 'Validador de XML', icon: CheckCircle, path: '/text/xml-validator', description: 'Verificação de sintaxe', status: 'active' },
    { id: 'sql-formatter', name: 'Formatador de SQL', icon: Database, path: '/text/sql-formatter', description: 'Formatação de queries', status: 'active' },
    { id: 'csv-formatter', name: 'Formatador de CSV', icon: FileText, path: '/text/csv-formatter', description: 'Formatação de dados', status: 'active' },
    { id: 'text-converter', name: 'Conversor de Texto', icon: Type, path: '/text/text-converter', description: '16 operações diferentes', status: 'active' },
    { id: 'emoji-picker', name: 'Seletor de Emojis', icon: Smile, path: '/text/emoji-picker', description: 'Copie e pesquise emojis', status: 'active' },
    { id: 'string-obfuscator', name: 'Ofuscador de String', icon: EyeOff, path: '/text/string-obfuscator', description: 'Ofusque textos e segredos', status: 'active' },
    { id: 'numeronym-generator', name: 'Gerador de Numerônimos', icon: Hash, path: '/text/numeronym-generator', description: 'Crie abreviações numéricas', status: 'active' },
    { id: 'ascii-art-generator', name: 'Gerador de ASCII Art', icon: Palette, path: '/text/ascii-art-generator', description: 'Crie arte com caracteres', status: 'active' },
    { id: 'font-style-generator', name: 'Gerador de Textos Estilizados', icon: Palette, path: '/text/font-style-generator', description: 'Crie textos estilizados', status: 'active' }
  ];

  // Detectar aba ativa pela URL
  const getActiveTab = () => {
    const match = tabs.find(tab => location.pathname.startsWith(tab.path));
    return match ? match.id : 'json-formatter';
  };
  const activeTab = getActiveTab();

  // Redirecionar /text para /text/json-formatter
  useEffect(() => {
    if (location.pathname === '/text') {
      navigate('/text/json-formatter');
    }
  }, [location.pathname, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Texto
        </h1>
        <p className="text-gray-600">
          Formatadores de JSON, XML, SQL, CSV e conversores de texto
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="json-formatter" element={<JSONFormatter />} />
        <Route path="json-validator" element={<JSONValidator />} />
        <Route path="xml-formatter" element={<XMLFormatter />} />
        <Route path="xml-validator" element={<XMLValidator />} />
        <Route path="sql-formatter" element={<SQLFormatter />} />
        <Route path="csv-formatter" element={<CSVFormatter />} />
        <Route path="text-converter" element={<TextConverter />} />
        <Route path="emoji-picker" element={<EmojiPicker />} />
        <Route path="string-obfuscator" element={<StringObfuscator />} />
        <Route path="numeronym-generator" element={<NumeronymGenerator />} />
        <Route path="ascii-art-generator" element={<AsciiArtGenerator />} />
        <Route path="font-style-generator" element={<FontStyleGenerator />} />
      </Routes>
    </div>
  );
};

// Componente Formatador de JSON
const JSONFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [indentSize, setIndentSize] = useState(2);

  const formatJSON = () => {
    if (!input.trim()) {
      toast.error('Digite um JSON para formatar');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setOutput(formatted);
    } catch (error) {
      toast.error('JSON inválido');
    }
  };

  const minifyJSON = () => {
    if (!input.trim()) {
      toast.error('Digite um JSON para minificar');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      const minified = JSON.stringify(parsed);
      setOutput(minified);
    } catch (error) {
      toast.error('JSON inválido');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              JSON de Entrada
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder='{"name": "John", "age": 30, "city": "New York"}'
              className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              JSON Formatado
            </label>
            <textarea
              value={output}
              readOnly
              placeholder="O JSON formatado aparecerá aqui..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Indentação: {indentSize} espaços
            </label>
            <input
              type="range"
              min="2"
              max="8"
              value={indentSize}
              onChange={(e) => setIndentSize(Number(e.target.value))}
              className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <button
            onClick={formatJSON}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Formatar JSON
          </button>

          <button
            onClick={minifyJSON}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            Minificar JSON
          </button>

          {output && (
            <button
              onClick={() => copyToClipboard(output)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Validador de JSON
const JSONValidator = () => {
  const [input, setInput] = useState('');
  const [validation, setValidation] = useState(null);

  const validateJSON = () => {
    if (!input.trim()) {
      toast.error('Digite um JSON para validar');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      setValidation({
        isValid: true,
        message: 'JSON válido',
        parsed: parsed
      });
    } catch (error) {
      setValidation({
        isValid: false,
        message: `JSON inválido: ${error.message}`,
        error: error
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            JSON para Validar
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='{"name": "John", "age": 30, "city": "New York"}'
            className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
          />
        </div>

        <button
          onClick={validateJSON}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Validar JSON
        </button>

        {validation && (
          <div className={`p-4 rounded-lg ${
            validation.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center space-x-2 mb-2">
              {validation.isValid ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
              <span className="font-medium">{validation.message}</span>
            </div>
            {validation.isValid && (
              <div className="mt-2">
                <div className="text-sm font-medium text-gray-900 mb-1">
                  Estrutura do JSON:
                </div>
                <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto">
                  {JSON.stringify(validation.parsed, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Formatador de XML
const XMLFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [indentSize, setIndentSize] = useState(2);

  const formatXML = () => {
    if (!input.trim()) {
      toast.error('Digite um XML para formatar');
      return;
    }

    try {
      // Função simples para formatar XML
      const formatXMLString = (xml, indent = 2) => {
        let formatted = '';
        let indentLevel = 0;
        const indentStr = ' '.repeat(indent);
        
        xml = xml.replace(/>\s*</g, '><');
        
        for (let i = 0; i < xml.length; i++) {
          const char = xml[i];
          const nextChar = xml[i + 1];
          
          if (char === '<' && nextChar === '/') {
            formatted += '\n' + indentStr.repeat(indentLevel - 1) + char;
            indentLevel--;
          } else if (char === '<') {
            formatted += '\n' + indentStr.repeat(indentLevel) + char;
            indentLevel++;
          } else if (char === '>') {
            formatted += char;
            if (nextChar === '<' && nextChar !== '/') {
              indentLevel++;
            }
          } else {
            formatted += char;
          }
        }
        
        return formatted.trim();
      };

      const formatted = formatXMLString(input, indentSize);
      setOutput(formatted);
    } catch (error) {
      toast.error('Erro ao formatar XML');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              XML de Entrada
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder='<root><item>value</item></root>'
              className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              XML Formatado
            </label>
            <textarea
              value={output}
              readOnly
              placeholder="O XML formatado aparecerá aqui..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Indentação: {indentSize} espaços
            </label>
            <input
              type="range"
              min="2"
              max="8"
              value={indentSize}
              onChange={(e) => setIndentSize(Number(e.target.value))}
              className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <button
            onClick={formatXML}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Formatar XML
          </button>

          {output && (
            <button
              onClick={() => copyToClipboard(output)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Validador de XML
const XMLValidator = () => {
  const [input, setInput] = useState('');
  const [validation, setValidation] = useState(null);

  const validateXML = () => {
    if (!input.trim()) {
      toast.error('Digite um XML para validar');
      return;
    }

    try {
      // Função simples para validar XML básico
      const validateXMLString = (xml) => {
        const stack = [];
        const regex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
        let match;
        
        while ((match = regex.exec(xml)) !== null) {
          const tag = match[1];
          const isClosing = match[0].startsWith('</');
          
          if (!isClosing) {
            stack.push(tag);
          } else {
            if (stack.length === 0 || stack.pop() !== tag) {
              throw new Error('Tags não balanceadas');
            }
          }
        }
        
        if (stack.length > 0) {
          throw new Error('Tags não fechadas');
        }
        
        return true;
      };

      validateXMLString(input);
      setValidation({
        isValid: true,
        message: 'XML válido'
      });
    } catch (error) {
      setValidation({
        isValid: false,
        message: `XML inválido: ${error.message}`
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            XML para Validar
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='<root><item>value</item></root>'
            className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
          />
        </div>

        <button
          onClick={validateXML}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          Validar XML
        </button>

        {validation && (
          <div className={`p-4 rounded-lg ${
            validation.isValid 
              ? 'bg-green-50 text-green-700' 
              : 'bg-red-50 text-red-700'
          }`}>
            <div className="flex items-center space-x-2">
              {validation.isValid ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
              <span className="font-medium">{validation.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Formatador de SQL
const SQLFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');

  const formatSQL = () => {
    if (!input.trim()) {
      toast.error('Digite uma consulta SQL para formatar');
      return;
    }

    try {
      // Função simples para formatar SQL
      const formatSQLString = (sql) => {
        const keywords = [
          'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'ORDER BY', 'GROUP BY',
          'HAVING', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN',
          'INSERT INTO', 'UPDATE', 'DELETE FROM', 'CREATE TABLE',
          'ALTER TABLE', 'DROP TABLE', 'INDEX', 'PRIMARY KEY'
        ];
        
        let formatted = sql.toUpperCase();
        
        // Adicionar quebras de linha antes de palavras-chave
        keywords.forEach(keyword => {
          const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
          formatted = formatted.replace(regex, `\n${keyword}`);
        });
        
        // Limpar espaços extras e quebras de linha
        formatted = formatted.replace(/\s+/g, ' ').trim();
        formatted = formatted.replace(/\n\s+/g, '\n');
        
        return formatted;
      };

      const formatted = formatSQLString(input);
      setOutput(formatted);
    } catch (error) {
      toast.error('Erro ao formatar SQL');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              SQL de Entrada
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="SELECT * FROM users WHERE age > 18 ORDER BY name"
              className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              SQL Formatado
            </label>
            <textarea
              value={output}
              readOnly
              placeholder="O SQL formatado aparecerá aqui..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={formatSQL}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Formatar SQL
          </button>

          {output && (
            <button
              onClick={() => copyToClipboard(output)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Formatador de CSV
const CSVFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [delimiter, setDelimiter] = useState(',');

  const formatCSV = () => {
    if (!input.trim()) {
      toast.error('Digite dados CSV para formatar');
      return;
    }

    try {
      const lines = input.trim().split('\n');
      const formattedLines = lines.map(line => {
        const fields = line.split(delimiter);
        return fields.map(field => `"${field.trim()}"`).join(delimiter);
      });
      
      const formatted = formattedLines.join('\n');
      setOutput(formatted);
    } catch (error) {
      toast.error('Erro ao formatar CSV');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              CSV de Entrada
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="nome,idade,cidade&#10;João,25,São Paulo&#10;Maria,30,Rio de Janeiro"
              className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              CSV Formatado
            </label>
            <textarea
              value={output}
              readOnly
              placeholder="O CSV formatado aparecerá aqui..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none font-mono text-sm"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Delimitador
            </label>
            <select
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value=",">Vírgula (,)</option>
              <option value=";">Ponto e vírgula (;)</option>
              <option value="\t">Tab (\t)</option>
              <option value="|">Pipe (|)</option>
            </select>
          </div>

          <button
            onClick={formatCSV}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Formatar CSV
          </button>

          {output && (
            <button
              onClick={() => copyToClipboard(output)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente Conversor de Texto
const TextConverter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [conversionType, setConversionType] = useState('uppercase');

  const conversionTypes = [
    { value: 'uppercase', label: 'MAIÚSCULAS' },
    { value: 'lowercase', label: 'minúsculas' },
    { value: 'capitalize', label: 'Primeira Letra' },
    { value: 'titlecase', label: 'Título' },
    { value: 'camelcase', label: 'camelCase' },
    { value: 'snakecase', label: 'snake_case' },
    { value: 'kebabcase', label: 'kebab-case' },
    { value: 'reverse', label: 'Inverter' },
    { value: 'remove-spaces', label: 'Remover Espaços' },
    { value: 'remove-newlines', label: 'Remover Quebras' }
  ];

  const convertText = () => {
    if (!input.trim()) {
      toast.error('Digite um texto para converter');
      return;
    }

    let converted = input;

    switch (conversionType) {
      case 'uppercase':
        converted = input.toUpperCase();
        break;
      case 'lowercase':
        converted = input.toLowerCase();
        break;
      case 'capitalize':
        converted = input.replace(/\b\w/g, l => l.toUpperCase());
        break;
      case 'titlecase':
        converted = input.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        break;
      case 'camelcase':
        converted = input.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
        break;
      case 'snakecase':
        converted = input.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '_');
        break;
      case 'kebabcase':
        converted = input.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '-');
        break;
      case 'reverse':
        converted = input.split('').reverse().join('');
        break;
      case 'remove-spaces':
        converted = input.replace(/\s+/g, '');
        break;
      case 'remove-newlines':
        converted = input.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
        break;
      default:
        converted = input;
    }

    setOutput(converted);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-full">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Texto de Entrada
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Digite o texto que deseja converter..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Texto Convertido
            </label>
            <textarea
              value={output}
              readOnly
              placeholder="O texto convertido aparecerá aqui..."
              className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 resize-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tipo de Conversão
          </label>
          <select
            value={conversionType}
            onChange={(e) => setConversionType(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {conversionTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={convertText}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Converter Texto
          </button>

          {output && (
            <button
              onClick={() => copyToClipboard(output)}
              className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <Copy size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Novo componente: Gerador de Textos Estilizados
const fontStyles = [
  {
    name: 'Negrito',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D400 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D41A + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Itálico',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D434 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D44E + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Negrito Itálico',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D468 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D482 + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Monoespaçado',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D670 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D68A + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Duplo',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D538 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D552 + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Small Caps',
    map: c => c.toUpperCase()
  },
  {
    name: 'Espaçado',
    map: c => c + ' '
  },
  {
    name: 'Subscrito',
    map: c => {
      const subs = { '0':'₀','1':'₁','2':'₂','3':'₃','4':'₄','5':'₅','6':'₆','7':'₇','8':'₈','9':'₉','a':'ₐ','e':'ₑ','h':'ₕ','i':'ᵢ','j':'ⱼ','k':'ₖ','l':'ₗ','m':'ₘ','n':'ₙ','o':'ₒ','p':'ₚ','r':'ᵣ','s':'ₛ','t':'ₜ','u':'ᵤ','v':'ᵥ','x':'ₓ'};
      return subs[c] || c;
    }
  },
  {
    name: 'Sobrescrito',
    map: c => {
      const sups = { '0':'⁰','1':'¹','2':'²','3':'³','4':'⁴','5':'⁵','6':'⁶','7':'⁷','8':'⁸','9':'⁹','a':'ᵃ','b':'ᵇ','c':'ᶜ','d':'ᵈ','e':'ᵉ','f':'ᶠ','g':'ᵍ','h':'ʰ','i':'ⁱ','j':'ʲ','k':'ᵏ','l':'ˡ','m':'ᵐ','n':'ⁿ','o':'ᵒ','p':'ᵖ','r':'ʳ','s':'ˢ','t':'ᵗ','u':'ᵘ','v':'ᵛ','w':'ʷ','x':'ˣ','y':'ʸ','z':'ᶻ'};
      return sups[c] || c;
    }
  },
  {
    name: 'Fraktur',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D504 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D51E + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Script',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D49C + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D4B6 + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Sans Serif',
    map: c => String.fromCodePoint(c.match(/[A-Z]/) ? 0x1D5A0 + (c.codePointAt(0) - 0x41) : c.match(/[a-z]/) ? 0x1D5BA + (c.codePointAt(0) - 0x61) : c.codePointAt(0))
  },
  {
    name: 'Zalgo',
    map: c => c + '\u0301\u0300\u0323\u0332' // acentos combinados para efeito glitch
  }
];

function stylize(text, mapFn) {
  return Array.from(text).map(mapFn).join('');
}

const FontStyleGenerator = () => {
  const [input, setInput] = useState('nServers');
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-gray-900">Gerador de Textos Estilizados</h2>
      <p className="mb-4 text-gray-600">Digite um texto e veja versões estilizadas com fontes Unicode, prontas para copiar e colar.</p>
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="Digite seu texto..."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fontStyles.map(style => (
          <div key={style.name} className="bg-gray-50 rounded-lg p-4 flex flex-col items-start">
            <div className="font-semibold text-gray-800 mb-2">{style.name}</div>
            <div className="font-mono text-lg break-all mb-2">{stylize(input, style.map)}</div>
            <button
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              onClick={() => {navigator.clipboard.writeText(stylize(input, style.map))}}
            >
              Copiar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Text; 